import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../model/contexts/user.context";
import { resetUserPassword } from "../../../api/users.api";

import "./UserProfile.scss";

const defaultFormFields = {
  email: "",
  password: "",
  newPassword: "",
};

const UserProfile = () => {
  const [formFields, setFormFields] = useState(defaultFormFields);
  const { email, password, newPassword } = formFields;

  const [message, setMessage] = useState(
    "Account updated. Please log in again with your new password."
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { user, removeCurrentUserAccount } = useUser();

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const trimmedNewPassword = newPassword.trim();

    console.log("[RESET PASSWORD SUBMIT START]", {
      email: trimmedEmail,
      currentPasswordLength: trimmedPassword.length,
      newPasswordLength: trimmedNewPassword.length,
    });

    if (!trimmedEmail) {
      setErrorMessage("Please input a valid email.");
      return;
    }

    if (!trimmedEmail.includes("@")) {
      setErrorMessage("Please make sure you input a valid email.");
      return;
    }

    if (!trimmedPassword) {
      setErrorMessage("Please input your current password.");
      return;
    }

    if (!trimmedNewPassword) {
      setErrorMessage("Please input a new password.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const response = await resetUserPassword({
        email: trimmedEmail,
        password: trimmedPassword,
        newPassword: trimmedNewPassword,
      });

      console.log("[RESET PASSWORD RESPONSE]", response);

      if (!response?.success) {
        setErrorMessage("Failed to update password.");
        return;
      }

      resetFormInput();
      setIsUpdated(true);
      setMessage("Password updated successfully. Please log in again.");
    } catch (error) {
      console.error("[RESET PASSWORD FRONTEND ERROR]", {
        email: trimmedEmail,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });

      setErrorMessage(
        error?.response?.data?.message || "Failed to update password."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormFields((prevFields) => ({
      ...prevFields,
      [name]: value,
    }));
  };

  const resetFormInput = () => {
    setFormFields(defaultFormFields);
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete your account? This action cannot be undone."
    );

    if (!confirmed) return;

    try {
      await removeCurrentUserAccount(user?.uid);
      navigate("/auth");
    } catch (error) {
      console.error("Delete account failed:", error);
      alert("Failed to delete account. Please try again.");
    }
  };

  return (
    <div className="user-profile-content-container">
      {!isUpdated ? (
        <>
          <div className="user-profile-header">
            <h2>Settings</h2>
            <p>Manage your account and password</p>
          </div>

          <div className="settings-section">
            <div className="settings-section-header">
              <h3>Account Settings</h3>
              <span className="settings-section-subtext">
                Update your password or delete your account.
              </span>
            </div>

            <div className="user-profile-menu">
              <h2>User Profile</h2>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Current Email Username (Required):</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your current email username"
                    value={email}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Current Password (Required):</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Enter your current password"
                    value={password}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Reset Password:</label>
                  <input
                    type="password"
                    name="newPassword"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={handleChange}
                  />
                </div>

                {errorMessage && (
                  <p style={{ color: "red", marginTop: "10px" }}>
                    {errorMessage}
                  </p>
                )}

                <div className="settings-actions">
                  {email.trim() && password.trim() && newPassword.trim() && (
                    <button
                      type="submit"
                      className="update-form-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Updating..." : "Update Password"}
                    </button>
                  )}

                  <button
                    type="button"
                    className="delete-account-button"
                    onClick={handleDeleteAccount}
                  >
                    Delete Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      ) : (
        <div className="updated-settings-verification-message">
          <h1>Password updated.</h1>
          <p>{message}</p>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
